import { Button, Col, Layout, message, Row, Space, Tabs, Typography } from "antd";
import TabPane from "antd/es/tabs/TabPane";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import SwipeHandUp from "../../../assets/gif/finger-animation.gif";
import ActiveCollectionIcon from "../../../assets/icons/active-collection.svg";
import ActiveGridIcon from "../../../assets/icons/active-grid.svg";
import BookmarkFilledOtherProfileIcon from "../../../assets/icons/bookmark-filled-other-profile.svg";
import BookmarkOtherProfileIcon from "../../../assets/icons/bookmark-other-profile.svg";
import ChatIcon from "../../../assets/icons/chat-icon.svg";
import ChevronBackOtherProfileIcon from "../../../assets/icons/chevron-back-other-profile.svg";
import CollectionIcon from "../../../assets/icons/collection.svg";
import GridIcon from "../../../assets/icons/grid.svg";
import LocationIcon from "../../../assets/icons/location.svg";
import ShareOtherProfileIcon from "../../../assets/icons/share-other-profile.svg";
import VerifyIcon from "../../../assets/icons/verify.svg";
import AvatarComponent from "../../../components/AvatarComponent";
import CollectionGridComponent from "../../../components/Collections/CollectionGridComponent";
import PresentationVideoButton from "../../../components/discover/common/PresentationVideoButton";
import FanProfileTrainingOverlayComponent from "../../../components/FanProfileTrainingOverlayComponent";
import FanReportCreatorModalComponent from "../../../components/FanReportCreatorModalComponent";
import FooterComponent from "../../../components/FooterComponent";
import ShareModalComponent from "../../../components/ShareModalComponent";
import ThreeDotsComponent from "../../../components/ThreeDotsComponent";
import { CREATOR, FAN, FOOTER_IDS, MAX_NAME_LENGTH } from "../../../helpers/constant";
import { IConversation } from "../../../helpers/types";
import useProfileCustomPageInfos from "../../../hooks/global/useProfileCustomPageInfos";
import useFetchOtherUserInfos from "../../../hooks/userHooks/useFetchOtherUserInfos";
import useFetchUserSettings from "../../../hooks/userHooks/useFetchUserSettings";
import { resetFanDiscovers } from "../../../redux/discovers/discoversSlice";
import { getConversationById, selectConversation } from "../../../redux/messages/messagesSlice";
import { AppDispatch } from "../../../redux/store";
import { updateUserReaction, updateUserSettingsStatus } from "../../../redux/users/usersSlice";
import { messageService, reactionService, reportService } from "../../../services";
import { SUBSCRIPTION_STATUS } from "../../../utls/constants";
import FeedGridComponent from "./FeedGridComponent";
import ProfileEvent from "./ProfileEvent";

type Props = {
  username: string;
  currentUserRole: string;
  currentUserId: string;
  handleNavigate: () => void;
};

const OtherUserProfilePage: React.FC<Props> = ({ username, currentUserRole, currentUserId, handleNavigate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { otherUser: user, error } = useFetchOtherUserInfos(username);
  const { currentUserSettings } = useFetchUserSettings();
  useProfileCustomPageInfos({ username, t });
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>(localStorage.getItem("activeProfileTab") || user?.userSettings?.defaultPage || "feeds");
  const [showOverlay, setShowOverlay] = useState(false);
  const [fanModalStep, setFanModalStep] = useState(1);
  const iconSize = user && user.infos.name.length > MAX_NAME_LENGTH ? "16px" : "22px";
  const [isFollowed, setIsFollowed] = useState<boolean>(user?.infos.isFollowed || false);

  useEffect(() => {
    if (currentUserSettings) {
      setShowOverlay(!currentUserSettings?.hasSeenFanProfileModal || false);
    }
  }, [currentUserSettings?.hasSeenFanProfileModal]);

  useEffect(() => {
    if (user && user.userSettings) {
      setActiveTab(localStorage.getItem("activeProfileTab") || user.userSettings.defaultPage || "feeds");
    } else {
      setActiveTab(localStorage.getItem("activeProfileTab") || "feeds");
    }
  }, [user]);

  useEffect(() => {
    if (user?.infos?.isFollowed !== undefined) {
      setIsFollowed(user.infos.isFollowed);
    }
  }, [user?.infos?.isFollowed]);

  const followOrUnFollow = (creatorId: string) => {
    const payload = {
      action: "follow",
      objectId: creatorId,
      objectType: "creator",
    };
    if (user?.infos.isFollowed) {
      reactionService.deactivatedReaction(payload).then((result) => {
        dispatch(
          updateUserReaction({
            id: creatorId,
            isFollowed: false,
          })
        );
      });
    } else {
      reactionService.createReaction(payload).then((result) => {
        dispatch(
          updateUserReaction({
            id: creatorId,
            isFollowed: true,
          })
        );
      });
    }
  };

  const onSubscribeClick = () => {
    user && user?.infos?.subscriptionStatus === SUBSCRIPTION_STATUS.DEACTIVATED && navigate(`/common/subscribe/${user?.id}`);
  };

  const startConversation = async () => {
    if (!user) return;

    let urlToNavigate = currentUserRole === CREATOR ? "/creator/conversation/new" : "/fan/requestMedia/new";
    const conversationId = await messageService.isConversationExist({
      fanId: currentUserId,
      creatorId: user.infos._id,
    });
    if (conversationId === null) {
      const newConversation: IConversation = {
        _id: "new",
        fanCanSendMessage: true,
        interlocutor: {
          userId: user.infos._id,
          avatar: user.infos.avatar,
          name: user.infos.name,
          username: user.infos.username,
          role: CREATOR,
        },
        currentUser: {
          userId: currentUserId,
          avatar: "",
          name: "",
          role: FAN,
        },
        chat: user.userSettings?.chat,
      };
      dispatch(selectConversation(newConversation));
      urlToNavigate = urlToNavigate + `?newConversation=${JSON.stringify(newConversation)}`;
    } else {
      await dispatch(getConversationById(conversationId));
      urlToNavigate = urlToNavigate.replace("new", conversationId);
    }
    navigate(urlToNavigate, { replace: false });
  };

  async function onReportClick() {
    if (!user) return;
    const payload = {
      target: "creator",
      targetId: user.id,
      title: "report",
      description: "",
    };
    const resp = await reportService.createReport(payload);
    if (resp?.data) {
      message.success(t("report-success"));
    } else if (resp?.message) {
      message.error(resp.message);
    }
    setIsReportModalOpen(false);
  }

  const handleTabChange = (key: string) => {
    localStorage.setItem("activeProfileTab", key);
    setActiveTab(key);
  };

  const fanModalNextStep = () => {
    if (fanModalStep === 1) {
      setFanModalStep(2);
    } else {
      if (!currentUserSettings?.hasSeenFanProfileModal) {
        dispatch(updateUserSettingsStatus("hasSeenFanProfileModal"));
      }
    }
  };

  const goPresentationVideos = () => {
    dispatch(resetFanDiscovers());
    navigate(`/common/presentation/videos/${username}`);
  };

  const handleToggle = () => {
    setIsFollowed(!isFollowed);
  };
  const Icon = isFollowed ? BookmarkFilledOtherProfileIcon : BookmarkOtherProfileIcon;

  return (
    <Layout className="gradient-background-profile no-scroll-horizontal">
      <Layout.Content className="content fanProfileContentWrapper background-transparent">
        <Row className="header-container">
          <img
            src={ChevronBackOtherProfileIcon}
            width={10}
            alt="arrow left"
            onClick={() => {
              handleNavigate();
            }}
          />
          <h2 className="header-text-other-profile">@{username}</h2>

          <div>
            <img
              src={Icon}
              className="mr-15"
              width={16}
              alt="bookmark"
              onClick={() => {
                handleToggle();
                followOrUnFollow(user?.id || "");
              }}
            />
            <img className="mr-10" src={ShareOtherProfileIcon} width={20} alt="share" onClick={() => setIsShareModalOpen(true)} />
          </div>
        </Row>
        <div className="fanProfileTopWrapper">
          <Row align="middle">
            <Col className="colAvatar mr-10">
              <AvatarComponent image={user?.infos.avatar || ""} size={105} fontSize={16} fontWeight={true} isLargeView={true} />
            </Col>
            <Col className="colDetails">
              <Row className="nameRow" align="middle">
                <Typography.Text className={`nameTitleOtherProfile`}>
                  {error !== "user-not-found" ? <>{user?.infos.name}</> : t(error as string)}
                </Typography.Text>
                {user?.infos.verifiedAccount && <img className="verifyIcon" src={VerifyIcon} width={iconSize} alt="verify" />}
              </Row>

              <Row className="mt-5" align="middle">
                <Space>
                  <Button
                    className={`subscribeBtn ${user?.infos?.subscriptionStatus === SUBSCRIPTION_STATUS?.DEACTIVATED ? "" : "unsubscribe"}`}
                    onClick={() => onSubscribeClick()}
                  >
                    {t(
                      `${user?.infos?.subscriptionStatus === SUBSCRIPTION_STATUS?.DEACTIVATED ? (user?.infos?.isSubscribed === true ? "extend-subscription" : "Subscribe-without-obligation") : "subscriber"}`
                    )}
                  </Button>
                  <img
                    className="mt-3"
                    width={44}
                    src={ChatIcon}
                    onClick={() => {
                      startConversation();
                    }}
                    alt="chat"
                  />
                </Space>
              </Row>
            </Col>
          </Row>
        </div>
        <div className="labelWrapper">
          {user?.infos.city && (
            <Row className="m-5">
              <Col className="colValues location">
                <img className="locationImg mr-5" src={LocationIcon} width={16} alt="location" />
                <Typography.Text className="fanProfileValue">{user.infos.city}</Typography.Text>
              </Col>
            </Row>
          )}
          {user?.userSettings?.hasDiscovers && <PresentationVideoButton onClick={goPresentationVideos} notificationVisible={false} />}
          <Row className="mt-10">
            <div
              className="fanProfileDescriptionLabel"
              dangerouslySetInnerHTML={{
                __html: (user?.infos.description || "").replace(/\n/g, "<br />"),
              }}
            />
          </Row>

          <Row className="mt-10 max-w-80 justify-content-between" align="middle">
            <div className="leftItems">
              <Space>
                <Col
                  className="colValues"
                  onClick={() => {
                    handleTabChange("publications");
                  }}
                >
                  <Typography.Text className="fanProfileAttributeValue">{user?.infos.stats?.totalFeeds || 0}</Typography.Text>
                  <Typography.Text className="fanProfileValue">{t("publications")}</Typography.Text>
                </Col>
              </Space>

              <Space>
                <Col
                  className="colValues"
                  onClick={() => {
                    handleTabChange("collections");
                  }}
                >
                  <Typography.Text className="fanProfileAttributeValue">{user?.infos.stats?.totalCollections || 0}</Typography.Text>
                  <Typography.Text className="fanProfileValue">{t("collections")}</Typography.Text>
                </Col>
              </Space>
            </div>

            <ThreeDotsComponent onClick={() => setIsReportModalOpen(true)} />
          </Row>
        </div>

        {user && <ProfileEvent isMyProfile={false} username={username} creatorId={user?.id!} userSettings={user?.userSettings!} />}

        <Col className={`feeds-container ${showOverlay ? "overlay-visible" : ""}`}>
          <Tabs activeKey={activeTab} onChange={showOverlay ? fanModalNextStep : handleTabChange}>
            <TabPane
              tab={
                <div className="tab-container">
                  <img
                    src={fanModalStep === 2 && showOverlay ? ActiveGridIcon : !showOverlay && activeTab === "feeds" ? ActiveGridIcon : GridIcon}
                    width={showOverlay ? 30 : 20}
                    alt="publications icon"
                    className={`${fanModalStep !== 2 && showOverlay ? "opacity-icon" : ""}`}
                  />
                  {fanModalStep === 2 && showOverlay && <img className="swipe-icon" src={SwipeHandUp} alt="swipe up" />}
                </div>
              }
              key="feeds"
            >
              <FeedGridComponent isMyProfile={false} creatorId={user?.id!} subscribed={user?.infos.isSubscribed || false} />
            </TabPane>

            <TabPane
              tab={
                <div className="tab-container">
                  <img
                    src={
                      fanModalStep === 1 && showOverlay
                        ? ActiveCollectionIcon
                        : !showOverlay && activeTab === "collection"
                          ? ActiveCollectionIcon
                          : CollectionIcon
                    }
                    width={showOverlay ? 30 : 20}
                    className={`${fanModalStep !== 1 && showOverlay ? "opacity-icon" : ""}`}
                    alt="collections icon"
                  />
                  {fanModalStep === 1 && showOverlay && <img className="swipe-icon" src={SwipeHandUp} alt="swipe up" />}
                </div>
              }
              key="collection"
            >
              <CollectionGridComponent
                isMyProfile={false}
                creatorId={user?.id}
                username={username}
                goToPublicationTab={() => handleTabChange("feeds")}
              />
            </TabPane>
          </Tabs>
        </Col>
        {showOverlay && <FanProfileTrainingOverlayComponent step={fanModalStep} fanModalStep={fanModalNextStep} />}
        <ShareModalComponent isOpen={isShareModalOpen} onClose={() => setIsShareModalOpen(false)} id={currentUserId} username={username} />
        <FanReportCreatorModalComponent
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          onReportClick={() => onReportClick()}
        />
      </Layout.Content>
      <FooterComponent page={FOOTER_IDS.SEARCH} />
    </Layout>
  );
};
export default OtherUserProfilePage;
